/**
 * ServiceTechniqueNav — bandeau de navigation Service technique unifié.
 *
 * Jumeau de GrcNav (accent « tech »). Remplace les 4 barres de nav divergentes
 * qui coexistaient dans la brique (ServiceTechniqueTabs, ServiceTechniqueLayout
 * /SubNavPill, nav inline de PlanningManagerPage, SubTabLink de Signalements/
 * Arrêtés) — dont certaines avaient un `overflow-x-auto` qui forçait le
 * défilement horizontal sur mobile au lieu de passer les onglets à la ligne.
 *
 * Onglets (manager / admin / super_admin) :
 *   Planning (menu déroulant : Planning de travail · Présence) · Fiches incident
 *   · Arrêtés · Véhicules · Matériel
 *
 * Pour l'agent : uniquement le menu déroulant « Planning » (Ma tournée +
 * Présence) et « Fiches incident » — il ne voit que ce à quoi il a accès.
 *
 * Contraintes reprises de GrcNav :
 *   - flex-wrap (jamais d'overflow-x-auto) → les onglets passent à la ligne
 *   - text-xs + pills h-9 → même taille que les autres briques
 *   - menu déroulant en position fixe (échappe overflow / backdrop-blur / sticky)
 *   - ferme au clic extérieur, au scroll et au resize
 */
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';

export type ServiceTechniqueNavKey =
  | 'planning' | 'presence' | 'incidents' | 'arretes' | 'vehicules' | 'materiel' | 'edl_templates'
  | 'visites_erp' | 'contrats' | 'cles' | 'locaux' | 'reglages';

function Tab({
  children, active, onClick,
}: {
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}) {
  if (active) {
    return (
      <span className="inline-flex h-9 items-center gap-1 rounded-r-pill border border-tech/30 bg-tech-tint/80 px-3 text-xs font-medium text-tech-fg backdrop-blur-glass">
        {children}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-9 items-center gap-1 rounded-r-pill border border-transparent bg-transparent px-3 text-xs font-medium text-ink-muted backdrop-blur-glass transition-all hover:border-border hover:bg-surface/40 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
    >
      {children}
    </button>
  );
}

export function ServiceTechniqueNav({
  active,
  /** Contenu optionnel poussé à droite (ex: navigation semaine, boutons). Passe
      à la ligne via flex-wrap sur mobile — plus de décalage hors cadre. */
  right,
}: {
  active: ServiceTechniqueNavKey;
  right?: React.ReactNode;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const isAgent = user?.role === 'agent';
  const canManage =
    user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'manager';

  // Planning UNIFIÉ (20/07) : un seul onglet « Planning » — la présence et le
  // travail (lieux/postes) cohabitent DANS la page, via un sélecteur de sous-vue
  // (Travail · Présence · Année). Fini le menu déroulant Présence/Travail, qui
  // provoquait le bug de z-index en mobile (le menu passait sous les briques).
  return (
    <div role="navigation" aria-label="Service technique" className="mx-auto flex max-w-6xl flex-wrap items-center gap-2">
      {/*
        24/07 — l'agent a désormais DEUX entrées distinctes, parce qu'elles ne
        servent pas la même chose :

        · « Ma tournée » = la vue TERRAIN. Compteurs À faire / Terminés /
          Partiels et accès aux fiches d'intervention (c'est là, et seulement
          là, qu'on clôture avec photos). Propre au service technique.

        · « Planning » = la même grille Équipe / Individuelle que le manager,
          en LECTURE SEULE — celle qu'on retrouve à l'identique dans toutes les
          autres briques.

        Les fusionner ferait perdre les compteurs d'intervention ; n'afficher
        que la tournée privait l'agent de la vue d'équipe.
      */}
      {isAgent && (
        <Tab
          active={active === 'planning'}
          onClick={() => navigate('/service-technique/ma-tournee')}
        >
          Ma tournée
        </Tab>
      )}

      <Tab
        active={isAgent ? active === 'presence' : (active === 'planning' || active === 'presence')}
        onClick={() => navigate('/service-technique/planning')}
      >
        Planning
      </Tab>

      <Tab
        active={active === 'incidents'}
        onClick={() => navigate('/service-technique/signalements')}
      >
        Fiches incident
      </Tab>

      {/* Arrêtés / Véhicules / Matériel réservés aux gestionnaires : on ne les
          affiche pas pour l'agent (comme les autres briques). */}
      {!isAgent && (
        <>
          <Tab
            active={active === 'arretes'}
            onClick={() => canManage && navigate('/service-technique/arretes')}
          >
            Arrêtés
          </Tab>
          {/* M24 (retour Manon ; maquette validée le 24/09) — « État des locaux »
              REMPLACE les onglets « États des lieux » et « Clés » : plan, clés et
              états des lieux au même endroit. Les anciens écrans restent
              atteignables depuis celui-ci (liens « Gérer les clés », « Modèles »),
              et restent actifs dans le menu quand on y est. */}
          <Tab
            active={active === 'locaux' || active === 'edl_templates' || active === 'cles'}
            onClick={() => navigate('/service-technique/locaux')}
          >
            État des locaux
          </Tab>
          {/* Suivi ERP : rattaché au parc bâtimentaire, géré par le technique. */}
          <Tab
            active={active === 'visites_erp'}
            onClick={() => navigate('/service-technique/visites-erp')}
          >
            Sécurité ERP
          </Tab>
          {/* Contrats & baux (item 7.5) : même parc, même service. */}
          <Tab
            active={active === 'contrats'}
            onClick={() => navigate('/service-technique/contrats')}
          >
            Contrats &amp; baux
          </Tab>
          <Tab active={active === 'vehicules'} onClick={() => navigate('/parc')}>
            Véhicules
          </Tab>
          <Tab active={active === 'materiel'} onClick={() => navigate('/materiel')}>
            Location
          </Tab>
          {/* Refonte UX, chantier 1 — « Réglages » toujours en dernier. */}
          <Tab active={active === 'reglages'} onClick={() => navigate('/service-technique/reglages')}>
            Réglages
          </Tab>
        </>
      )}

      {right && <div className="ml-auto flex flex-wrap items-center gap-2">{right}</div>}
    </div>
  );
}
